import axios from "axios";
import { getCockpitConfigs } from "config";
import { getAuthData, type AuthData } from "models/AuthModel";
import { TCellMemoPostData, TCellMemoCardData } from "models/CellMemoModel";
import { TRoute } from "models/RouteModel";
import { TSharedPage } from "models/SharedPageModel";
import type { TUser } from "models/UserModel";
import { sleep } from "modules/misc";

const { configs, baseUrl } = getCockpitConfigs();

export const apiAxiosOptions = () => {
  const authData = getAuthData();
  return {
    withCredentials: true,
    headers: { "X-CSRF-Token": authData ? authData.csrf_token : null },
  };
};

export const apiSignIn = async (userName: any, password: any) => {
  const result = await axios
    .post(
      `${baseUrl}/user/login?_format=json`,
      {
        name: userName,
        pass: password,
      },
      apiAxiosOptions(),
    )
    .then((response) => {
      return {
        status: "success",
        response,
      };
    })
    .catch((error) => {
      return {
        status: "fail",
        response: error.response,
      };
    });
  return result;
};

export const apiSignOut = async (authData: AuthData) => {
  const token = authData && authData.logout_token;
  const result = await axios
    .post(`${baseUrl}/user/logout?_format=json&token=${token}`, {}, apiAxiosOptions())
    .then((response) => {
      return {
        status: "success",
        response,
      };
    })
    .catch((error) => {
      console.log(error);
      return {
        status: "fail",
        response: error.response,
      };
    });
  return result;
};

export const apiFetchUser = async () => {
  const authData = getAuthData();
  const uid = authData && authData.current_user.uid;
  if (!uid) return null;
  const result = await axios
    .get(`${baseUrl}/user/${uid}?_format=json`, apiAxiosOptions())
    .then(async (response) => {
      await sleep(0);
      const user = apiBuildUser(response.data);
      return { status: "success", raw: response.data, user };
    })
    .catch((error) => {
      console.log(error);
      return { status: "fail", raw: error.response, user: null };
    });
  return result;
};

export const apiBuildUser = (data: any): TUser => {
  const menus = data.field_cp_menus_json[0] ? data.field_cp_menus_json[0]["value"] : false;
  const routes = data.field_cp_routes_json[0] ? data.field_cp_routes_json[0]["value"] : false;
  const cards = data.field_cp_cards_json[0] ? data.field_cp_cards_json[0]["value"] : false;
  const layouts = data.field_cp_layouts_json[0] ? data.field_cp_layouts_json[0]["value"] : false;
  return {
    uid: data.uid[0]["value"],
    menus: menus ? JSON.parse(menus) : [],
    routes: routes
      ? JSON.parse(routes).map((r: any) => {
          return { ...r, id: String(r.id), url: "/" + r.id, parentId: r.parentId ? String(r.parentId) : undefined };
        })
      : [],
    cards: cards ? JSON.parse(cards) : [],
    layouts: cards ? JSON.parse(layouts) : {},
    availableFunctions: data.field_cp_available_functions
      ? data.field_cp_available_functions.map((row: any) => row["value"])
      : [],
    firstName: data.field_first_name[0] ? data.field_first_name[0]["value"] : null,
    lastName: data.field_last_name[0] ? data.field_last_name[0]["value"] : null,
    shitenCD: data.field_shiten_cd[0] ? data.field_shiten_cd[0]["value"] : null,
    avatar_file_id: data.user_picture[0] ? data.user_picture[0]["target_id"] : null,
    avatar_url: data.user_picture[0] ? data.user_picture[0]["url"] : null,
  };
};

const apiFileUpload = async (url: string, file: File) => {
  const options = JSON.parse(JSON.stringify(apiAxiosOptions()));
  const fileName = encodeURI(file.name);
  options.headers["Content-Type"] = "application/octet-stream";
  options.headers["Content-Disposition"] = `file; filename="${fileName}"`;
  const result = await axios
    .post(`${baseUrl}/${url}?_format=json`, file, options)
    .then((response) => {
      const data = response.data;
      data["file_id"] = data["fid"][0]["value"];
      data["url"] = `${configs.api.schema}://${configs.api.host}${data["uri"][0]["url"]}`;
      return {
        status: "success",
        data,
      };
    })
    .catch((error) => {
      return {
        status: "fail",
        response: error.response,
      };
    });
  return result;
};

export const apiUploadUserAvatar = async (file: File) => {
  const result = await apiFileUpload("file/upload/user_entity/user/user/user_picture", file);
  return result;
};

export const apiPatchUser = async (orgData: any, newData: any) => {
  const authData = getAuthData();
  if (!authData) return;

  const editedData = { ...orgData };

  Object.keys(newData).forEach((key) => {
    switch (key) {
      case "routes":
        editedData["field_cp_routes_json"] = [{ value: JSON.stringify(newData[key]) }];
        break;
      case "cards":
        editedData["field_cp_cards_json"] = [{ value: JSON.stringify(newData[key]) }];
        break;
      case "layouts":
        editedData["field_cp_layouts_json"] = [{ value: JSON.stringify(newData[key]) }];
        break;
      case "avatar_file_id":
        if (newData[key]) editedData["user_picture"] = [{ target_id: newData[key] }];
        break;
      default:
        editedData[`field_${key}`] = [{ value: newData[key] }];
        break;
    }
  });

  delete editedData.changed;
  const result = await axios
    .patch(`${baseUrl}/user/${editedData.uid[0]["value"]}?_format=json`, editedData, apiAxiosOptions())
    .then((response) => {
      return {
        status: "success",
        response,
      };
    })
    .catch((error) => {
      return {
        status: "fail",
        response: error.response,
      };
    });
  return result;
};

export const apiFetchCardMasters = async () => {
  const result = await axios.get(`${baseUrl}/api/card-masters`, apiAxiosOptions()).then(async (response) => {
    await sleep(0);
    return response.data;
  });
  return result;
};

export const apiFetchSearchMasters = async () => {
  const result = await axios.get(`${baseUrl}/api/search-masters`, apiAxiosOptions()).then(async (response) => {
    await sleep(0);
    return response.data;
  });
  return result;
};

export const apiFetchTotalMasters = async () => {
  const result = await axios.get(`${baseUrl}/api/total-masters`, apiAxiosOptions()).then(async (response) => {
    return response.data;
  });
  return result;
};

export const apiFetchDisplayMasters = async () => {
  const result = await axios.get(`${baseUrl}/api/display-masters`, apiAxiosOptions()).then(async (response) => {
    return response.data;
  });
  return result;
};

export const apiFetchFieldMasters = async () => {
  const result = await axios.get(`${baseUrl}/api/target-masters`, apiAxiosOptions()).then(async (response) => {
    return response.data;
  });
  return result;
};

// [ToDo] #21185 の結果、どこでも使わなくなった。Drupal のAPIリソースごと削除しても良いかも。
export const apiFetchSharedPageBaseRoute = async (id?: string) => {
  if (!id) return;
  const result = await axios
    .get(`${baseUrl}/api/share-page`, { ...apiAxiosOptions(), params: { id } })
    .then(async (response) => {
      await sleep(0);
      return response.data;
    });
  return result;
};

export const apiFetchSharedPage = async (id: TSharedPage["id"]) => {
  const result = await axios
    .get(`${baseUrl}/share-page/${id}?_format=json`, apiAxiosOptions())
    .then(async (response) => {
      return response.data;
    });
  return {
    id: String(result.id[0]["value"]),
    status: result.status[0]["value"],
    createUserId: String(result.field_create_user[0]["target_id"]),
    routeId: String(result.field_route_id[0]["value"]),
    title: result.label[0]["value"],
    baseRoute:
      result.field_base_route && result.field_base_route[0] ? JSON.parse(result.field_base_route[0]["value"]) : null,
    contents: result.field_contents && result.field_contents[0] ? JSON.parse(result.field_contents[0]["value"]) : null,
    shitenCD: result.field_shiten_cd && result.field_shiten_cd[0] ? result.field_shiten_cd[0] : null,
  } as TSharedPage;
};

export const apiFetchSharedPages = async () =>
  await axios.get(`${baseUrl}/api/share-pages`, apiAxiosOptions()).then((response) => response.data as TSharedPage[]);

export const apiFetchEasySharedPages = async (uid: string, routeId: string) =>
  await axios.get(`${baseUrl}/api/share-base-page`, { ...apiAxiosOptions(), params: { uid, route_id: routeId } }).then(
    async (response) =>
      response.data as {
        route: TRoute;
        contents: TSharedPage["contents"];
      },
  );

export const apiSaveSharedPage = async (mode: "post" | "patch", data: TSharedPage) => {
  const authData = getAuthData();
  if (!authData) return;

  const configs = {
    post: { method: axios.post, url: `${baseUrl}/entity/share_page?_format=json` },
    patch: { method: axios.patch, url: `${baseUrl}/share-page/${data.id}?_format=json` },
  };

  const config = configs[mode];

  const postData = {
    label: [{ value: data.title }],
    field_create_user: [{ target_id: data.createUserId }],
    field_route_id: [{ value: data.routeId }],
    status: [{ value: data.status === false ? false : true }],
    field_contents: [{ value: JSON.stringify(data.contents) }],
    field_base_route: [{ value: JSON.stringify(data.baseRoute) }],
    field_shiten_cd: [{ value: data.shitenCD }],
  };

  const result = await config
    .method(config.url, postData, apiAxiosOptions())
    .then((response) => {
      return {
        status: "success",
        response,
      };
    })
    .catch((error) => {
      return {
        status: "fail",
        response: error.response,
      };
    });
  return result;
};

export const apiFetchCellMemos = async (enable: boolean, cardData: TCellMemoCardData) => {
  if (!enable || !cardData) return [];
  const result = await axios.post(`${baseUrl}/api/cell-memos`, cardData, apiAxiosOptions()).then(async (response) => {
    await sleep(1000);
    return response.data;
  });
  return result;
};

export const apiPostCellMemos = async (data: TCellMemoPostData) => {
  const authData = getAuthData();
  if (!authData) return;
  const postData = {
    label: [{ value: `${data.totalMasterId}/${data.aggregate}/${data.fieldId}/${data.subField}/${data.date}` }],
    field_total_master: [{ target_id: data.totalMasterId }],
    field_aggregate: [{ value: data.aggregate }],
    field_field: [{ target_id: data.fieldId }],
    field_subfield: [{ value: data.subField }],
    field_date: [{ value: data.date }],
    field_content: [{ value: data.content }],
  };
  const result = await axios
    .post(`${baseUrl}/entity/cell_memo?_format=json`, postData, apiAxiosOptions())
    .then((response) => {
      return {
        status: "success",
        response,
      };
    })
    .catch((error) => {
      return {
        status: "fail",
        response: error.response,
      };
    });
  return result;
};
